import React from "react";
import { ErrorMessage, LoadingIndicator } from "@jsq/ds/core";
import { BaseFullScreenTakeover } from "../../Components/BaseFullScreenTakeover";
import { PaymentPrefConfirmation } from "../../Components/PaymentPrefConfirmation";
import { UpdatePositionsWithPaymentPrefPositionList } from "../../Components/PositionList/UpdatePositionsWithPaymentPrefPositionList";
import { AssociatePaymentPrefMutationTypeEnum } from "../../Context/enums";
import { PaymentPrefEditForAccountAndPositionWorkflow } from "../PaymentPrefEditForAccountAndPositionWorkflow";
import { usePaymentPrefEditForAccountWorkflowController } from "./controller";
import { type PaymentPrefEditForAccountWorkflowProps } from "./types";

/**
 * A workflow component that handles the editing of payment preferences at the account level.
 * It manages data fetching and form presentation for editing existing payment preferences.
 *
 * @component
 *
 * @param {Object} props - Component props
 * @param {string} props.accountId - ID of the account to edit payment preferences for
 * @param {string} props.sourcePaymentPrefId - ID of the payment preference to edit
 * @param {() => void} props.onSuccess - Function called when the workflow completes successfully
 * @param {() => void} props.onExit - Function called when the workflow is exited
 * @param {Arena} [props.arena] - Arena configuration object
 *
 * @example
 * ```tsx
 * <PaymentPrefEditForAccountWorkflow
 *   accountId="123"
 *   sourcePaymentPrefId="456"
 *   onSuccess={handleSuccess}
 *   onExit={() => setShowWorkflow(false)}
 *   arena={{ features: { hasPositionPaymentPrefStaffApprovals: true }}}
 * />
 * ```
 *
 * @remarks
 * - Uses BaseFullScreenTakeover for modal-like presentation
 * - Handles loading and error states
 * - Integrates with PaymentPrefEditForAccountAndPositionWorkflow for form presentation
 * - Supports updating multiple positions with the same payment preference
 */
export const PaymentPrefEditForAccountWorkflow: React.FC<
  PaymentPrefEditForAccountWorkflowProps
> = ({ accountId, sourcePaymentPrefId, onSuccess, onExit, arena }) => {
  const { dataLoading, dataError, existingPaymentPref, accountName } =
    usePaymentPrefEditForAccountWorkflowController({ accountId, sourcePaymentPrefId });

  if (dataLoading) {
    return <LoadingIndicator />;
  }

  if (dataError || !existingPaymentPref) {
    return <ErrorMessage>Failed to load</ErrorMessage>;
  }

  return (
    <BaseFullScreenTakeover title="Update payment instructions" onExit={onExit}>
      <PaymentPrefEditForAccountAndPositionWorkflow
        position={{ accountId, id: "", investmentEntityId: "", investmentEntityName: "" }}
        existingPaymentPref={existingPaymentPref}
        initialAssociatePaymentPref={
          AssociatePaymentPrefMutationTypeEnum.AllOtherPositionsWithPaymentPref
        }
        ListOfPositionsToUpdateComponent={UpdatePositionsWithPaymentPrefPositionList}
        onExit={onExit}
        onSuccess={onSuccess}
        suffixComponents={[PaymentPrefConfirmation]}
        accountName={accountName}
        arena={arena}
      />
    </BaseFullScreenTakeover>
  );
};

export type { PaymentPrefEditForAccountWorkflowProps };
